import { BaseQueryFn, FetchArgs, FetchBaseQueryError, createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { RootState } from 'redux/store';
import { BarragemF, ScaleF, SimulationDetailF, SimulationF, UsuarioF } from 'types';
import { Mutex } from 'async-mutex';
import { loggedOut, setUserLogged } from '../auth/auth-slice';
import config from 'config';
import { UserCreate, UserUpdate } from './types/usuario';
import { PasswordChange } from './types/account';
import { SimulacaoUpdate } from './types/simulacao';

export interface LoginResponse {
    refresh: string;
    access: string;
}

export interface LoginRequest {
    email: string;
    password: string;
}

const baseQuery = fetchBaseQuery({
    baseUrl: config.REACT_APP_API_URL,
    prepareHeaders: (headers, { getState }) => {
        const token = (getState() as RootState).Auth.access;
        if (token) {
            headers.set('authorization', `Bearer ${token}`);
        }
        return headers;
    },
});
export const getDownloadSimulationTiffsUrl = (id: string) =>
    `${config.REACT_APP_API_URL}simulation/${id}/tiffs/download`;

export const getDownloadSimulationErrorLogUrl = (id: string) =>
    `${config.REACT_APP_API_URL}simulation/${id}/error/download`;

export const getDownloadSimulationProjectUrl = (id: string) =>
    `${config.REACT_APP_API_URL}simulation/${id}/hecrasproject/download`;

const mutex = new Mutex();
const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
    args,
    api,
    extraOptions
) => {
    // wait until the mutex is available without locking it
    await mutex.waitForUnlock();
    let result = await baseQuery(args, api, extraOptions);
    if (result.error && result.error.status === 401) {
        // checking whether the mutex is locked
        if (!mutex.isLocked()) {
            const release = await mutex.acquire();
            try {
                const refreshResult = await baseQuery(
                    {
                        url: '/refresh',
                        method: 'POST',
                        body: {
                            refresh: (api.getState() as RootState).Auth.refresh,
                        },
                    },
                    api,
                    extraOptions
                );
                if (refreshResult.data) {
                    await api.dispatch(setUserLogged(refreshResult.data as LoginResponse));
                    await api.dispatch(serviceApi.endpoints.setCookie.initiate()).unwrap();
                    // retry the initial query
                    result = await baseQuery(args, api, extraOptions);
                } else {
                    api.dispatch(loggedOut());
                }
            } finally {
                // release must be called once the mutex should be released again.
                release();
            }
        } else {
            // wait until the mutex is available without locking it
            await mutex.waitForUnlock();
            result = await baseQuery(args, api, extraOptions);
        }
    }
    return result;
};
const allTags = ['User', 'Profile', 'Asset', 'Geo', 'Scale'];
export const serviceApi = createApi({
    reducerPath: 'serviceApi',
    baseQuery: baseQueryWithReauth,
    tagTypes: allTags,
    endpoints: (builder) => ({
        login: builder.mutation<LoginResponse, LoginRequest>({
            query: (credentials) => ({
                url: 'account/login',
                method: 'POST',
                body: credentials,
            }),
            invalidatesTags: allTags,
        }),
        tileRefresh: builder.mutation<LoginResponse, void>({
            async queryFn(_, api, _extraOptions, baseQuery) {
                const refresh = (api.getState() as any).Auth.refresh;

                if (!refresh) {
                    return { error: { status: 401, data: 'Missing refresh' } as any };
                }

                const refreshResult = await baseQuery({
                    url: 'account/refresh',
                    method: 'POST',
                    body: { refresh },
                });

                if (refreshResult.data) {
                    // ✅ dispatch correto aqui
                    api.dispatch(setUserLogged(refreshResult.data as LoginResponse));

                    return { data: refreshResult.data as LoginResponse };
                }

                return { error: refreshResult.error as FetchBaseQueryError };
            },
        }),
        setCookie: builder.mutation<void, void>({
            query: () => ({
                url: 'account/tile_cookie',
                method: 'POST',
                credentials: 'include',
            }),
        }),
        forgotPassword: builder.mutation<undefined, string>({
            query: (email) => ({
                url: 'account/forgotPassword',
                method: 'POST',
                body: {
                    email,
                },
            }),
        }),
        logout: builder.mutation<undefined, undefined>({
            query: () => ({
                url: 'account/logout',
                method: 'POST',
            }),
            invalidatesTags: allTags,
        }),
        profile: builder.query<UsuarioF, undefined>({
            query: () => ({ url: 'account/profile' }),
            providesTags: ['Profile'],
        }),
        changePassword: builder.mutation<undefined, PasswordChange>({
            query: ({ confirmPassword, old_password, password }) => ({
                url: 'account/change_pass',
                method: 'POST',
                body: {
                    password,
                    old_password,
                    confirmPassword,
                },
            }),
        }),
        updateUser: builder.mutation<UsuarioF, UserUpdate>({
            query: ({ id, ...data }) => ({
                url: `users/${id}`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: ['User', 'Profile'],
        }),
        getUsers: builder.query<UsuarioF[], undefined>({
            query: () => ({ url: 'users' }),
            providesTags: ['User'],
        }),
        getUser: builder.query<UsuarioF, UsuarioF['id']>({
            query: (id) => ({ url: `users/${id}` }),
            providesTags: ['User'],
        }),
        removeUser: builder.mutation<undefined, UsuarioF['id']>({
            query: (id) => ({
                url: `users/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['User'],
        }),
        createUser: builder.mutation<UsuarioF, UserCreate>({
            query: (data) => ({
                url: 'users',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['User'],
        }),
        getAssets: builder.query<BarragemF[], undefined>({
            query: () => ({ url: 'asset' }),
            providesTags: ['Asset'],
        }),
        getAsset: builder.query<BarragemF, BarragemF['id']>({
            query: (id) => ({ url: `asset/${id}` }),
            providesTags: ['Asset'],
        }),
        getSimulations: builder.query<SimulationF[], number | undefined>({
            query: (assetId) => {
                let url = 'simulation';
                if (assetId !== undefined) url += `?assetId=${assetId}`;
                return url;
            },
            async onQueryStarted(arg, { queryFulfilled, dispatch }) {
                const { data: simulations } = await queryFulfilled;
                if (simulations.some(({ loading }) => loading)) {
                    setTimeout(() => {
                        dispatch(serviceApi.util.invalidateTags(['Geo']));
                    }, 5000);
                }
            },
            providesTags: ['Geo'],
        }),
        getLayerScale: builder.query<ScaleF, void>({
            query: () => ({ url: `simulation/scale` }),
            providesTags: ['Scale']
        }),
        createSimulation: builder.mutation<SimulationF, any>({
            query: ({ assetId, ...data }) => ({
                url: `asset/${assetId}/simulate`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Geo'],
        }),
        updateSimulationInputData: builder.mutation<SimulationF, any>({
            query: ({ id, ...data }) => ({
                url: `simulation/${id}/resimulate`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Geo'],
        }),
        getSimulation: builder.query<SimulationDetailF, SimulationF['id']>({
            query: (id) => `simulation/${id}`,
            providesTags: (result) => (result ? [{ type: 'Geo', id: result.id }] : []),
            async onQueryStarted(arg, { queryFulfilled, dispatch }) {
                const { data: simulation } = await queryFulfilled;
                if (simulation.loading) {
                    setTimeout(() => {
                        dispatch(serviceApi.util.invalidateTags([{ type: 'Geo', id: arg }]));
                    }, 5000);
                }
            },
        }),
        downloadSimulationTiffs: builder.query<any, SimulationF['id']>({
            query: (id) => ({
                url: `simulation/${id}/tiffs/download`,
                responseHandler: async (response) => {
                    return await response.blob();
                },
            }),
        }),
        downloadSimulationErrorLog: builder.query<any, SimulationF['id']>({
            query: (id) => ({
                url: `simulation/${id}/error/download`,
                responseHandler: async (response) => {
                    return await response.blob();
                },
            }),
        }),
        downloadSimulationProject: builder.query<any, SimulationF['id']>({
            query: (id) => ({
                url: `simulation/${id}/hecrasproject/download`,
                responseHandler: async (response) => {
                    return await response.blob();
                },
            }),
        }),
        updateSimulation: builder.mutation<SimulationF, SimulacaoUpdate>({
            query: ({ id, name }) => ({
                url: `simulation/${id}`,
                method: 'PATCH',
                body: {
                    name,
                },
            }),
            invalidatesTags: ['Geo'],
        }),
        removeSimulation: builder.mutation<undefined, SimulationF['id']>({
            query: (id) => ({
                url: `simulation/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Geo'],
        }),
    }),
});

export const {
    useUpdateSimulationInputDataMutation,
    useRemoveSimulationMutation,
    useUpdateSimulationMutation,
    useCreateSimulationMutation,
    useChangePasswordMutation,
    useForgotPasswordMutation,
    useGetSimulationsQuery,
    useGetLayerScaleQuery,
    useLazyGetLayerScaleQuery,
    useGetSimulationQuery,
    useSetCookieMutation,
    useTileRefreshMutation,
    useLazyDownloadSimulationTiffsQuery,
    useLazyDownloadSimulationProjectQuery,
    useLazyDownloadSimulationErrorLogQuery,
    useCreateUserMutation,
    useUpdateUserMutation,
    useRemoveUserMutation,
    useGetAssetsQuery,
    useLogoutMutation,
    useGetAssetQuery,
    useLoginMutation,
    useGetUsersQuery,
    useProfileQuery,
    useGetUserQuery,
} = serviceApi;
